/** FNV-1a 32-bit hash over the full name string for a wide, stable hash space. */
function hashName(name) {
  let h = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h;
}

function colorFromName(name) {
  if (!name) return 'hsl(0,0%,45%)';
  const h = hashName(name);
  const hue = h % 360;
  const saturation = 45 + ((h >> 8) % 30); // 45–74%
  const lightness = 35 + ((h >> 16) % 20); // 35–54%
  return `hsl(${hue},${saturation}%,${lightness}%)`;
}

/**
 * Generate a 7×7 symmetrical pixel grid from the name hash.
 * Steps:
 *  1. Extract 28 bits from the hash (7 rows × 4 cols).
 *  2. Mirror columns so the layout is [0,1,2,3,2,1,0] → 7 wide.
 */
function generateGrid(name) {
  if (!name) return Array.from({ length: 5 }, () => Array(5).fill(false));
  const h = hashName(name);
  // Mix in a second hash pass so bits are well-distributed.
  const h2 = hashName(name.split('').reverse().join(''));
  const combined = ((h ^ (h2 << 14)) >>> 0) | ((h2 ^ (h >>> 14)) >>> 0);

  const grid = [];
  for (let row = 0; row < 5; row++) {
    // Each row uses 3 bits from bits [row*3 .. row*3+2]
    const rowBits = [];
    for (let col = 0; col < 3; col++) {
      rowBits.push(((combined >> (row * 3 + col)) & 1) === 1);
    }
    // Mirror: [0,1,2,1,0]
    grid.push([rowBits[0], rowBits[1], rowBits[2], rowBits[1], rowBits[0]]);
  }
  return grid;
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const getPersonnelIconName = (personOrName) => {
  if (typeof personOrName === 'string') return personOrName;
  return (
    personOrName?.name ||
    personOrName?.fullName ||
    `${personOrName?.firstName ?? ''} ${personOrName?.lastName ?? ''}`.trim() ||
    `${personOrName?.first_name ?? ''} ${personOrName?.last_name ?? ''}`.trim() ||
    ''
  );
};

export const getPersonnelIconColor = (personOrName) => {
  const name = getPersonnelIconName(personOrName);
  return colorFromName(name);
};

export const getPersonnelInitials = (personOrName) => {
  const name = getPersonnelIconName(personOrName);
  return getInitials(name);
};

export const PersonnelIcon = ({
  onClick = () => {},
  person,
  size = 36,
  isHighlighted = false,
  showName = false,
  showInitials = true,
  iconStyle = 'basic',
  inverted = false,
}) => {
  const color = colorFromName(person?.name);
  const initials = getInitials(person?.name);
  const highlightOutline = isHighlighted
    ? `2px solid ${inverted ? '#fff' : '#64aaff'}`
    : '2px solid transparent';

  const basicIcon = (
    <div
      className="rounded-pill d-flex align-items-center justify-content-center fw-bold"
      title={person?.name}
      style={{
        width: size,
        height: size,
        background: inverted ? 'white' : color,
        outline: highlightOutline,
        outlineOffset: '1px',
        fontSize: size * 0.38,
        color: inverted ? color : '#fff',
        flexShrink: 0,
        userSelect: 'none',
        cursor: 'pointer',
      }}
    >
      {showInitials ? initials : null}
    </div>
  );

  const grid = iconStyle === 'devIcon' ? generateGrid(person?.name) : null;
  const pixelSize = size / 7;
  const litColor = inverted ? '#fff' : 'transparent';
  const darkColor = inverted ? color : '#111c27';

  const devIcon = (
    <div
      className="rounded-pill d-grid fw-bold"
      title={person?.name}
      style={{
        width: size,
        height: size,
        background: inverted ? 'white' : color,
        outline: highlightOutline,
        outlineOffset: '1px',
        padding: pixelSize,
        gridTemplateColumns: `repeat(5, ${pixelSize}px)`,
        gridTemplateRows: `repeat(5, ${pixelSize}px)`,
        overflow: 'hidden',
        flexShrink: 0,
        userSelect: 'none',
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      {grid?.map((row, rowIdx) =>
        row.map((on, colIdx) => (
          <div
            key={`${rowIdx}-${colIdx}`}
            style={{
              width: pixelSize,
              height: pixelSize,
              background: on ? litColor : darkColor,
            }}
          />
        )),
      )}
      <div
        className="d-flex align-items-center justify-content-center fw-bold"
        style={{
          position: 'absolute',
          inset: 0,
          fontSize: size * 0.32,
          color: inverted ? color : '#fff',
          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          pointerEvents: 'none',
        }}
      >
        {showInitials ? initials : null}
      </div>
    </div>
  );

  const icon = iconStyle === 'devIcon' ? devIcon : basicIcon;

  if (!showName) {
    return (
      <div onClick={onClick} style={{ display: 'contents' }}>
        {icon}
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
    >
      {icon}
      <span style={{ color: '#fff', fontSize: size * 0.38, userSelect: 'none' }}>
        {person?.name || `User ${person?.userId}`}
      </span>
    </div>
  );
};
